import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { Release } from '@sunstone/api-client/gen/releases/v1/releases_pb';
import {
  useCheckReleaseDeprecateBlockers,
  useDeprecateRelease,
  useListReleases,
  useStreamReleaseEvents,
  useSyncRelease,
  useUpdateReleaseState,
} from '@sunstone/api-client/hooks';
import { Badge, Button, DataTable, Switch } from '@sunstone/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { Clock, GitBranch } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DeprecateReleaseDialog } from '@/components/ui/DeprecateReleaseDialog';
import { PageHeader } from '@/components/ui/PageHeader';
import { ReleaseHistorySheet } from '@/components/ui/ReleaseHistorySheet';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthorization, useAuthToken } from '@/features/auth';
import { useDebounce } from '@/hooks/useDebounce';
import { useListState } from '@/hooks/useListState';

const PAGE_SIZE = 100;

// ReleaseSyncStatus is a numeric enum on the wire; StatusBadge is keyed by
// the fully-prefixed string name (matching every other *_STATUS_* enum in
// the app - see workspaceStatusKey in features/workspaces/utils.ts).
function releaseSyncStatusKey(status: Release['syncStatus']): string {
  switch (status) {
    case 1:
      return 'RELEASE_SYNC_STATUS_NONE';
    case 2:
      return 'RELEASE_SYNC_STATUS_IN_PROGRESS';
    case 3:
      return 'RELEASE_SYNC_STATUS_SUCCEEDED';
    case 4:
      return 'RELEASE_SYNC_STATUS_FAILED';
    default:
      return 'RELEASE_SYNC_STATUS_NONE';
  }
}

/**
 * Admin "view all releases" surface (SUN-368): every release including
 * deprecated ones - deprecated rows are shown (badged) rather than hidden,
 * since they're retained in the DB for audit trail and that history should
 * stay reachable from this page. The create-workspace release picker
 * (NewWorkspacePage) is the one that restricts to deployable releases only.
 * Visible to every authenticated role; editing (enable/disable), syncing,
 * and deprecating are all pure permission checks (edit:release,
 * sync:release, deprecate:release), mirroring the backend's respective RPC
 * gates.
 */
export function ReleasesPage() {
  const { token } = useAuthToken();
  const { hasPermission } = useAuthorization();
  useStreamReleaseEvents(token);

  const { page, search, setPage, setSearch } = useListState(true);
  const debouncedSearch = useDebounce(search);

  const [historyReleaseId, setHistoryReleaseId] = useState<string | null>(null);
  const [historyVersion, setHistoryVersion] = useState('');
  const [deprecateTarget, setDeprecateTarget] = useState<Release | null>(null);

  const { data, isLoading, error } = useListReleases(token, 'baic', {
    deployableOnly: false,
    includeDeprecated: true,
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
  });
  const releases = data?.releases ?? [];
  const totalCount = data?.totalCount ?? 0;

  const updateReleaseState = useUpdateReleaseState(token);
  const deprecateRelease = useDeprecateRelease(token);
  const syncRelease = useSyncRelease(token);

  const { data: blockersData } = useCheckReleaseDeprecateBlockers(
    deprecateTarget?.releaseId,
    token,
    !!deprecateTarget,
  );
  const blockingWorkspaceCount = blockersData?.blockingWorkspaceCount ?? 0;

  function handleConfirmDeprecate() {
    if (!deprecateTarget) return;
    deprecateRelease.mutate(
      { releaseId: deprecateTarget.releaseId },
      {
        onSuccess: () => setDeprecateTarget(null),
        onError: (err) => toast.error(err.message ?? 'Failed to deprecate the release.'),
      },
    );
  }

  const columns = useMemo<ColumnDef<Release>[]>(
    () => [
      {
        id: 'version',
        header: 'Version',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium text-foreground">
              {row.original.version}
            </span>
            {row.original.isDeprecated && <Badge variant="secondary">Deprecated</Badge>}
          </div>
        ),
      },
      {
        id: 'repository',
        header: 'Repository',
        meta: { className: 'max-w-[220px]' },
        cell: ({ row }) => (
          <div className="min-w-0 font-mono text-sm">
            <div className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
              <GitBranch className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{row.original.repository}</span>
            </div>
            {row.original.branch && (
              <p className="truncate pl-5 text-xs text-muted-foreground/70">
                @{row.original.branch}
              </p>
            )}
          </div>
        ),
      },
      {
        id: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground hidden md:block">
            {row.original.createdAt
              ? timestampDate(row.original.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : '—'}
          </span>
        ),
      },
      {
        id: 'workspaces',
        header: 'Workspaces',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground tabular-nums">
            {row.original.workspaceCount > 0 ? row.original.workspaceCount : '—'}
          </span>
        ),
      },
      {
        id: 'sync',
        header: 'Sync',
        cell: ({ row }) => <StatusBadge status={releaseSyncStatusKey(row.original.syncStatus)} />,
      },
      {
        id: 'lastSyncedAt',
        header: 'Last synced',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground hidden md:block">
            {row.original.lastSyncedAt
              ? timestampDate(row.original.lastSyncedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '—'}
          </span>
        ),
      },
      {
        id: 'enabled',
        header: 'Enabled',
        cell: ({ row }) => (
          <Switch
            checked={row.original.isEnabled}
            onCheckedChange={(checked) =>
              updateReleaseState.mutate({ releaseId: row.original.releaseId, isEnabled: checked })
            }
            disabled={
              !hasPermission('edit:release') ||
              updateReleaseState.isPending ||
              row.original.isDeprecated
            }
            aria-label={`${row.original.isEnabled ? 'Disable' : 'Enable'} ${row.original.version}`}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground/60 hover:text-muted-foreground"
              title="View history"
              onClick={() => {
                setHistoryReleaseId(row.original.releaseId);
                setHistoryVersion(row.original.version);
              }}
            >
              <Clock className="h-4 w-4" />
            </Button>
            {hasPermission('sync:release') && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                disabled={
                  syncRelease.isPending || !row.original.isEnabled || row.original.isDeprecated
                }
                onClick={() => syncRelease.mutate({ releaseId: row.original.releaseId })}
              >
                Sync
              </Button>
            )}
            {hasPermission('deprecate:release') && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-destructive"
                disabled={row.original.isDeprecated}
                onClick={() => setDeprecateTarget(row.original)}
              >
                Deprecate
              </Button>
            )}
          </div>
        ),
      },
    ],
    [
      updateReleaseState.isPending,
      updateReleaseState.mutate,
      hasPermission,
      syncRelease.isPending,
      syncRelease.mutate,
    ],
  );

  if (error) {
    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <PageHeader title="Releases" description="Enable, disable, and deprecate BAIC releases." />
        <div className="flex items-center justify-center p-8 text-destructive">
          Failed to load releases.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="Releases"
        description="Enable, disable, and deprecate BAIC releases. Deprecated releases are shown here for reference but can no longer be selected for install or upgrade."
      />
      <DataTable
        columns={columns}
        data={releases}
        showSearch
        searchPlaceholder="Search version, branch, repository…"
        searchValue={search}
        onSearchChange={setSearch}
        isLoading={isLoading}
        totalCount={totalCount}
        currentPage={page}
        onPageChange={setPage}
        pageSize={PAGE_SIZE}
      />

      {historyReleaseId && (
        <ReleaseHistorySheet
          token={token}
          releaseId={historyReleaseId}
          version={historyVersion}
          onClose={() => setHistoryReleaseId(null)}
        />
      )}

      {deprecateTarget && (
        <DeprecateReleaseDialog
          open
          onOpenChange={(open) => !open && setDeprecateTarget(null)}
          version={deprecateTarget.version}
          releaseId={deprecateTarget.releaseId}
          blockingWorkspaceCount={blockingWorkspaceCount}
          onConfirm={handleConfirmDeprecate}
          isSubmitting={deprecateRelease.isPending}
        />
      )}
    </div>
  );
}
